/**
 * 微信 JS 出码页：保存二维码、复制链接。
 * 与模板 cashier/wxjsPay.html 同一套，给安卓系统浏览器、iOS Safari、微信内置页用。
 * 保存优先系统分享（可进相册），复制必须在点击当次同步完成，不能等剪贴板 Promise 失败后再补。
 */

export function isIosLike () {
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return true
  }
  return navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints || 0) > 1
}

export function saveHintText () {
  return isIosLike()
    ? '请长按图片，点「存储图像」或「添加到照片」'
    : '请长按图片，点「保存图片」'
}

function dataUrlToBlob (dataUrl) {
  const parts = String(dataUrl || '').split(',')
  if (parts.length < 2) {
    return null
  }
  const mimeMatch = parts[0].match(/:(.*?);/)
  const mime = (mimeMatch && mimeMatch[1]) || 'image/png'
  let binary
  try {
    binary = atob(parts[1])
  } catch (e) {
    return null
  }
  const len = binary.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: mime })
}

function pngFileFromDataUrl (dataUrl, fileName) {
  const blob = dataUrlToBlob(dataUrl)
  if (!blob) {
    return null
  }
  try {
    return new File([blob], fileName || 'wxpay-qr.png', { type: blob.type || 'image/png' })
  } catch (e) {
    return null
  }
}

function triggerDownload (file, fileName) {
  const url = URL.createObjectURL(file)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName || 'wxpay-qr.png'
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  if (a.parentNode) {
    a.parentNode.removeChild(a)
  }
  setTimeout(() => URL.revokeObjectURL(url), 2500)
}

/**
 * 必须在点击回调里同步调用。
 * share：系统分享里选存储图像，成功则不必再弹层。
 * overlay：长按保存。安卓系统浏览器同时触发一次下载。
 */
export function saveQrFromDataUrl (dataUrl, fileName) {
  const src = String(dataUrl || '')
  const name = fileName || 'wxpay-qr.png'
  const file = src.indexOf('data:') === 0 ? pngFileFromDataUrl(src, name) : null
  const overlaySrc = file ? URL.createObjectURL(file) : src
  const overlay = () => ({ mode: 'overlay', src: overlaySrc, revoke: !!file })

  if (file && navigator.canShare && navigator.share) {
    const payload = { files: [file], title: '支付码' }
    try {
      if (navigator.canShare(payload)) {
        return navigator.share(payload).then(() => {
          if (file) {
            URL.revokeObjectURL(overlaySrc)
          }
          return { mode: 'share', src: '', revoke: false }
        }).catch((err) => {
          if (err && err.name === 'AbortError') {
            if (file) {
              URL.revokeObjectURL(overlaySrc)
            }
            return { mode: 'cancel', src: '', revoke: false }
          }
          if (!isIosLike()) {
            try { triggerDownload(file, name) } catch (e) { /* 内置浏览器常忽略下载 */ }
          }
          return overlay()
        })
      }
    } catch (e) {
      /* 旧内核不认 files */
    }
  }
  if (file && !isIosLike()) {
    try { triggerDownload(file, name) } catch (e) { /* 忽略 */ }
  }
  return Promise.resolve(overlay())
}

function copyByExecCommand (text) {
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('autocomplete', 'off')
  ta.setAttribute('autocorrect', 'off')
  ta.setAttribute('autocapitalize', 'off')
  ta.setAttribute('spellcheck', 'false')
  ta.style.cssText = 'position:fixed;top:0;left:0;width:2em;height:2em;padding:0;margin:0;border:none;outline:none;box-shadow:none;background:transparent;font-size:16px;z-index:100;'
  document.body.appendChild(ta)
  const ios = isIosLike()
  if (ios) {
    ta.contentEditable = 'true'
    ta.readOnly = false
  } else {
    ta.readOnly = true
  }
  ta.focus()
  ta.select()
  try {
    ta.setSelectionRange(0, text.length)
  } catch (e) { /* 部分内核没有 setSelectionRange */ }
  if (ios) {
    const range = document.createRange()
    range.selectNodeContents(ta)
    const sel = window.getSelection()
    if (sel) {
      sel.removeAllRanges()
      sel.addRange(range)
    }
  }
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch (e) {
    ok = false
  }
  document.body.removeChild(ta)
  if (ios) {
    const sel = window.getSelection()
    if (sel) {
      sel.removeAllRanges()
    }
  }
  return ok
}

export function selectElementText (el) {
  if (!el || !window.getSelection) {
    return
  }
  const range = document.createRange()
  range.selectNodeContents(el)
  const sel = window.getSelection()
  sel.removeAllRanges()
  sel.addRange(range)
}

/**
 * 在点击回调里同步复制。苹果和微信内置页优先 execCommand，系统浏览器再补 Clipboard API。
 */
export function copyTextReliable (text) {
  const value = String(text || '').trim()
  if (!value) {
    return Promise.reject(new Error('暂无可复制内容'))
  }
  const legacyOk = copyByExecCommand(value)
  if (isIosLike()) {
    if (legacyOk) {
      return Promise.resolve()
    }
  } else if (legacyOk && !(navigator.clipboard && navigator.clipboard.writeText)) {
    return Promise.resolve()
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(value).catch(() => {
      if (legacyOk) {
        return undefined
      }
      return Promise.reject(new Error('复制失败'))
    })
  }
  if (legacyOk) {
    return Promise.resolve()
  }
  return Promise.reject(new Error('复制失败'))
}
